import { useState } from 'react'
import { FiX, FiClock, FiCalendar, FiAlertCircle, FiSend, FiUser } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../../config/api'
import { Btn, Pill } from '../../ui'
import { formatearFecha } from '../../utils/fechas'

/*
 * PropuestaMarcajeModal (V2) — DS v2.
 * Admin propone un marcaje para uno o varios días sin fichaje (multi-empleado).
 */

const PropuestaMarcajeModalV2 = ({ diasSeleccionados, onClose, onSuccess, buildApiUrl }) => {
  const [motivo, setMotivo] = useState('')
  const [horaEntrada, setHoraEntrada] = useState('')
  const [horaSalida, setHoraSalida] = useState('')
  const [loading, setLoading] = useState(false)
  const [progreso, setProgreso] = useState({ actual: 0, total: 0 })

  const apiUrl = buildApiUrl ? buildApiUrl('/correcciones/proponer') : `${API_URL}/correcciones/proponer`
  const esMasivo = diasSeleccionados.length > 1

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!motivo.trim()) { toast.error('Debes indicar el motivo de la propuesta'); return }
    if (!horaEntrada) { toast.error('Debes indicar la hora de entrada'); return }

    setLoading(true)
    setProgreso({ actual: 0, total: diasSeleccionados.length })
    const token = localStorage.getItem('token')
    let exitos = 0
    const fallos = []

    for (const dia of diasSeleccionados) {
      setProgreso(prev => ({ ...prev, actual: prev.actual + 1 }))
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            empleado_id: dia.empleado_id,
            fecha: dia.fecha,
            hora_entrada_corregida: horaEntrada,
            hora_salida_corregida: horaSalida || null,
            motivo
          })
        })
        if (!response.ok) {
          const data = await response.json()
          throw new Error(data.error || 'Error al enviar propuesta')
        }
        exitos++
      } catch (error) {
        fallos.push(`${dia.empleado_nombre} - ${formatearFecha(dia.fecha)}: ${error.message}`)
      }
    }

    setLoading(false)

    if (exitos > 0 && fallos.length === 0) {
      toast.success(exitos === 1 ? 'Propuesta de marcaje enviada al empleado' : `${exitos} propuestas de marcaje enviadas`)
      onSuccess()
    } else if (exitos > 0) {
      toast.success(`${exitos} de ${diasSeleccionados.length} propuestas enviadas`)
      toast.error(`Fallaron: ${fallos.join('; ')}`, { duration: 8000 })
      onSuccess()
    } else {
      toast.error(fallos.length === 1 ? fallos[0] : 'Error al enviar propuestas')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="ui-card max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="eyebrow mb-1">Propuesta admin</div>
            <h3 className="font-display text-[22px] text-[var(--ink)] leading-tight flex items-center gap-2">
              Proponer marcaje.
              {esMasivo && <Pill tone="brand">{diasSeleccionados.length} días</Pill>}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="p-1.5 rounded-soft text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-2)] transition-colors disabled:opacity-50"
            aria-label="Cerrar"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Lista de días/empleados */}
        <div className="p-4 bg-[var(--bg-2)] border-b border-[var(--line)]">
          {esMasivo ? (
            <div className="max-h-40 overflow-y-auto space-y-1.5">
              {diasSeleccionados.map((dia) => (
                <div key={dia.id} className="flex items-center gap-2 text-[12.5px] text-[var(--ink-2)]">
                  <FiUser size={12} className="flex-shrink-0 text-[var(--ink-3)]" />
                  <span className="font-medium">{dia.empleado_nombre}</span>
                  <span className="text-[var(--ink-3)]">·</span>
                  <FiCalendar size={12} className="flex-shrink-0 text-[var(--ink-3)]" />
                  <span className="font-mono tnum">{formatearFecha(dia.fecha)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-[13px] text-[var(--ink-2)]">
              <FiUser size={13} className="text-[var(--ink-3)]" />
              <span className="font-medium">{diasSeleccionados[0].empleado_nombre}</span>
              <span className="text-[var(--ink-3)]">·</span>
              <FiCalendar size={13} className="text-[var(--ink-3)]" />
              <span className="font-mono tnum">{formatearFecha(diasSeleccionados[0].fecha)}</span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1.5">Hora entrada *</label>
              <div className="relative">
                <FiClock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#0F8C66] pointer-events-none" />
                <input
                  type="time"
                  value={horaEntrada}
                  onChange={(e) => setHoraEntrada(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Hora salida</label>
              <div className="relative">
                <FiClock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#C13030] pointer-events-none" />
                <input
                  type="time"
                  value={horaSalida}
                  onChange={(e) => setHoraSalida(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-1.5">Motivo de la propuesta *</label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[13px] resize-none focus:outline-none focus:border-[var(--ink-3)]"
              placeholder="Explica al empleado por qué propones este marcaje…"
              required
              disabled={loading}
            />
          </div>

          <div className="flex items-start gap-2 p-3 rounded-soft bg-[rgba(245,158,11,.06)] border border-[rgba(245,158,11,.20)]">
            <FiAlertCircle className="text-[#A86A06] mt-0.5 flex-shrink-0" size={14} />
            <p className="text-[11.5px] text-[#A86A06]">
              {esMasivo
                ? 'Se enviará una propuesta individual a cada empleado. El fichaje solo se creará cuando el empleado la acepte.'
                : 'La propuesta será enviada al empleado para su aprobación. Si la acepta, se creará un fichaje para ese día.'}
            </p>
          </div>

          {loading && esMasivo && (
            <div className="text-center text-[12.5px] text-[var(--brand-1)] font-medium">
              Enviando {progreso.actual} de {progreso.total}…
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <Btn type="button" variant="ghost" className="flex-1" onClick={onClose} disabled={loading}>
              Cancelar
            </Btn>
            <Btn type="submit" variant="primary" className="flex-1" disabled={loading}>
              <FiSend size={13} />
              {loading
                ? 'Enviando…'
                : esMasivo
                  ? `Enviar ${diasSeleccionados.length} propuestas`
                  : 'Enviar propuesta'}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PropuestaMarcajeModalV2
